import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { login } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import LoginTransition from '../components/LoginTransition';
import { useUser } from '../contexts/UserContext';

// Memoized Loading Spinner Component
const LoadingSpinner = () => (
  <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white drop-shadow-sm" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { setLoggedIn } = useAuth();
  const { setUserDataManually } = useUser();
  
  // Consolidated form state
  const [formState, setFormState] = useState(() => {
    const savedUsername = localStorage.getItem('rememberedUsername');
    return {
      username: savedUsername || '',
      password: '',
      rememberMe: savedUsername !== null,
      error: '',
      loading: false
    };
  });

  // Optimized viewport height setter
  const setVH = useCallback(() => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }, []);

  // Login sayfasında overflow-y auto olsun ve viewport height'ı ayarla
  useEffect(() => {
    // Login sayfası mount olduğunda overflow-y: auto yap
    document.documentElement.style.overflowY = 'auto';
    
    // İlk yüklemede ve resize'da çalıştır
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    
    // Component unmount olduğunda temizle
    return () => {
      document.documentElement.style.overflowY = '';
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, [setVH]);

  // Memoized style classes
  const switchClassName = useMemo(() => 
    `${formState.rememberMe 
      ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 shadow-lg shadow-indigo-500/25' 
      : 'bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 shadow-sm'
    } relative inline-flex h-7 w-12 items-center rounded-full transition-transform duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 hover:scale-105 transform`,
  [formState.rememberMe]);

  const switchThumbClassName = useMemo(() => 
    `${formState.rememberMe ? 'translate-x-6' : 'translate-x-1'} inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-300 ease-in-out shadow-md ${formState.rememberMe ? 'shadow-indigo-500/30' : 'shadow-gray-400/40'}`,
  [formState.rememberMe]);

  // Memoized button content
  const buttonContent = useMemo(() => {
    if (formState.loading) {
      return (
        <div className="flex items-center relative z-10">
          <LoadingSpinner />
          <span className="drop-shadow-sm">Giriş Yapılıyor...</span>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center relative z-10">
        <span className="drop-shadow-sm">Giriş Yap</span>
      </div>
    );
  }, [formState.loading]);

  // Optimized form handlers
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState(prev => ({ ...prev, error: '', loading: true }));

    try {
      const { loginResponse, userData } = await login(formState.username, formState.password);
      
      if (loginResponse.success) {
        if (formState.rememberMe) {
          localStorage.setItem('rememberedUsername', formState.username);
        } else {
          localStorage.removeItem('rememberedUsername');
        }
        
        setLoggedIn(true);
        
        if (userData) {
          setUserDataManually(userData);
          navigate('/', { replace: true });
        } else {
          setFormState(prev => ({ 
            ...prev, 
            error: 'Kullanıcı bilgileri alınamadı, lütfen tekrar deneyin.',
            loading: false 
          }));
        }
      } else {
        setFormState(prev => ({ 
          ...prev, 
          error: loginResponse.msg,
          loading: false 
        }));
      }
    } catch (err) {
      setFormState(prev => ({ 
        ...prev, 
        error: 'Giriş yapılırken bir hata oluştu.',
        loading: false 
      }));
    }
  }, [formState.username, formState.password, formState.rememberMe, setLoggedIn, setUserDataManually, navigate]);

  const handleUsernameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormState(prev => ({ ...prev, username: e.target.value }));
  }, []);

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormState(prev => ({ ...prev, password: e.target.value }));
  }, []);

  const handleRememberMeToggle = useCallback(() => {
    setFormState(prev => ({ ...prev, rememberMe: !prev.rememberMe }));
  }, []);

  const handleRippleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const button = e.currentTarget;
    const ripple = document.createElement('span');
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.className = 'absolute bg-white/20 rounded-full scale-0 animate-ripple-login';
    
    button.appendChild(ripple);
    
    setTimeout(() => {
      ripple.remove();
    }, 600);
  }, []);

  return (
    <LoginTransition>
    <div className="min-h-screen-mobile flex flex-col items-center justify-center relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-indigo-900/30 lg:mx-auto lg:shadow-2xl lg:border lg:border-gray-200 lg:dark:border-gray-700">
      
      {/* Decorative background elements - Optimized for mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large gradient circles - Reduced on mobile */}
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-600/20 rounded-full blur-3xl hidden sm:block animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-pink-600/20 rounded-full blur-3xl hidden sm:block animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-20 left-20 w-60 h-60 bg-gradient-to-br from-cyan-400/10 to-blue-500/10 rounded-full blur-2xl hidden md:block animate-pulse" style={{animationDelay: '2s'}}></div>
        
        {/* Mobile simplified background circles */}
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-blue-400/15 to-indigo-600/15 rounded-full blur-2xl sm:hidden"></div>
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-tr from-purple-400/15 to-pink-600/15 rounded-full blur-2xl sm:hidden"></div>
        
        {/* Floating particles - Reduced on mobile */}
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-blue-400/30 rounded-full hidden sm:block animate-bounce" style={{animationDelay: '0.5s'}}></div>
        <div className="absolute top-3/4 right-1/4 w-3 h-3 bg-indigo-400/40 rounded-full hidden sm:block animate-bounce" style={{animationDelay: '1.5s'}}></div>
        <div className="absolute top-1/2 left-1/6 w-1.5 h-1.5 bg-purple-400/50 rounded-full hidden md:block animate-bounce" style={{animationDelay: '2.5s'}}></div>
        <div className="absolute top-1/3 right-1/3 w-2.5 h-2.5 bg-cyan-400/35 rounded-full hidden md:block animate-bounce" style={{animationDelay: '3s'}}></div>
        
        {/* Mobile simplified particles */}
        <div className="absolute top-1/3 right-1/4 w-1.5 h-1.5 bg-blue-400/40 rounded-full sm:hidden"></div>
        <div className="absolute top-2/3 left-1/4 w-2 h-2 bg-indigo-400/30 rounded-full sm:hidden"></div>
      </div>
      {/* Desktop tema düğmesi */}
      <button
        onClick={toggleTheme}
        className="hidden sm:block absolute top-4 right-4 z-50 p-2.5 rounded-xl text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-transform duration-200 ease-in-out shadow-sm hover:shadow-md group"
      >
        <span className="relative flex items-center justify-center w-6 h-6">
          {theme === 'light' ? (
            <svg className="w-5 h-5 transform transition-transform duration-200 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          ) : theme === 'dark' ? (
            <svg className="w-5 h-5 transform transition-transform duration-200 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 transform transition-transform duration-200 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          )}
        </span>
      </button>

      <div className="flex-1 flex items-center justify-center w-full py-8 relative z-10">
        <div className="max-w-md w-full mx-4">
          <motion.div 
            className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 dark:border-gray-700/50 overflow-hidden relative"
            initial={{ opacity: 0, scale: 0.98, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{
              duration: window.innerWidth < 768 ? 0.3 : 0.5,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
          >
            {/* Mobil tema düğmesi - login panelinin sağ üst köşesi */}
            <button
              onClick={toggleTheme}
              className="sm:hidden absolute top-2 right-2 z-10 p-2 rounded-lg text-gray-700 dark:text-gray-300 bg-gray-100/80 dark:bg-gray-700/80 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-transform duration-200 ease-in-out shadow-sm hover:shadow-md group"
            >
              <span className="relative flex items-center justify-center w-5 h-5">
                {theme === 'light' ? (
                  <svg className="w-4 h-4 transform transition-transform duration-200 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                ) : theme === 'dark' ? (
                  <svg className="w-4 h-4 transform transition-transform duration-200 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 transform transition-transform duration-200 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                )}
              </span>
            </button>
            
            <div className="px-8 pt-8 pb-6">
              <div className="mt-1 text-center">
                <motion.div 
                  className="flex items-center justify-center mb-4"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    duration: window.innerWidth < 768 ? 0.2 : 0.4,
                    delay: window.innerWidth < 768 ? 0.1 : 0.2,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                >
                  <img 
                    src="/bolat-logo-part-1.png" 
                    alt="Bolat Logo Part 1" 
                    className="h-10 w-auto"
                  />
                  <img 
                    src="/bolat-logo-part-2.png" 
                    alt="Bolat Logo Part 2" 
                    className="h-10 w-auto dark:brightness-0 dark:invert dark:opacity-80"
                  />
                </motion.div>
                <motion.p 
                  className="text-gray-600 dark:text-gray-400 text-md font-medium"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: window.innerWidth < 768 ? 0.2 : 0.3,
                    delay: window.innerWidth < 768 ? 0.15 : 0.4,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                >
                  Bayi Girişi
                </motion.p>
                
                {/* Decorative Divider */}
                <motion.div 
                  className="mt-6 mb-4"
                  initial={{ opacity: 0, scaleX: 0 }}
                  animate={{ opacity: 1, scaleX: 1 }}
                  transition={{
                    duration: window.innerWidth < 768 ? 0.3 : 0.5,
                    delay: window.innerWidth < 768 ? 0.2 : 0.6,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                >
                  <div className="h-px bg-gradient-to-r from-transparent via-indigo-300 to-transparent dark:via-indigo-500 w-24 mx-auto"></div>
                </motion.div>
              </div>

              <form className="mt-4 p-4 pb-6 space-y-6 space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Kullanıcı Adı
                    </label>
                    <input
                      id="username"
                      name="username"
                      type="text"
                      required
                      autoComplete="off"
                      className="form-input appearance-none block w-full"
                      placeholder="Kullanıcı adınızı girin"
                      value={formState.username}
                      onChange={handleUsernameChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Parola
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      autoComplete="off"
                      className="form-input appearance-none block w-full"
                      placeholder="Parolanızı girin"
                      value={formState.password}
                      onChange={handlePasswordChange}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <label htmlFor="remember-me" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer select-none">
                      Beni Hatırla
                    </label>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={formState.rememberMe}
                      onClick={handleRememberMeToggle}
                      className={switchClassName}
                    >
                      <span
                        className={switchThumbClassName}
                      />
                      {formState.rememberMe && (
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-400/20 to-indigo-600/20 animate-pulse"></div>
                      )}
                    </button>
                  </div>
                </div>

                {formState.error && (
                  <div className="bg-red-50 dark:bg-red-900/50 border-l-4 border-red-500 p-4 rounded">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-700 dark:text-red-200">{formState.error}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={formState.loading}
                    className={`
                      w-full flex items-center justify-center px-6 py-4
                      text-base font-bold text-white
                      bg-gradient-to-r from-indigo-500 via-indigo-600 to-purple-600
                      hover:from-indigo-600 hover:via-indigo-700 hover:to-purple-700
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                      rounded-xl shadow-xl shadow-indigo-500/30
                      transform transition-transform duration-200 ease-out
                      hover:scale-[1.02] sm:hover:scale-[1.03] hover:shadow-2xl hover:shadow-indigo-500/40
                      active:scale-[0.98]
                      disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100
                      disabled:hover:shadow-xl disabled:hover:shadow-indigo-500/30
                      relative overflow-hidden
                      border border-indigo-400/20
                      before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-500 sm:before:duration-700
                    `}
                    onClick={handleRippleClick}
                  >
                    {buttonContent}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="w-full flex items-center justify-center py-8 relative z-10">
        <motion.div 
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: window.innerWidth < 768 ? 0.4 : 0.6,
            delay: window.innerWidth < 768 ? 0.3 : 0.5,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          <h2 className="text-3xl font-extrabold bg-gradient-to-r from-indigo-600 to-blue-500 dark:from-indigo-400 dark:to-blue-300 bg-clip-text text-transparent tracking-tight">
            Hızlı Servis Pro
          </h2>
        </motion.div>
      </div>
    </div>
    </LoginTransition>
  );
} 